import React from "react";
import { Check } from "lucide-react";
import { PROJECT_STATUSES, STATUS_CAPTIONS } from "../lib/constants";
import { useSettings } from "../lib/settings";

/**
 * StatusTimeline — architectural horizontal stepper.
 * When isOnHold=true, the rail dims and retains the previous status highlight.
 * Renders a plain-English caption below the active step.
 * Caption source priority: settings.status_captions[key] → STATUS_CAPTIONS default.
 */
export const StatusTimeline = ({ status, isOnHold, previousStatus }) => {
  const { settings } = useSettings();
  const visibleStatus = isOnHold ? previousStatus || "pending" : status;
  const activeIndex = PROJECT_STATUSES.findIndex((s) => s.key === visibleStatus);

  // Check explicitly for undefined/null so that an empty string ("") clears
  // the caption rather than falling back to the default wording.
  const captionFor = (key) => {
    const custom = settings?.status_captions?.[key];
    if (custom !== undefined && custom !== null) return custom;
    return STATUS_CAPTIONS[key] || "";
  };

  return (
    <div className={`relative ${isOnHold ? "opacity-50 grayscale" : ""}`} data-testid="status-timeline">
      {/* connector line */}
      <div className="absolute left-0 right-0 top-[15px] h-px bg-white/10" />
      <ol className="relative grid grid-cols-6 gap-2">
        {PROJECT_STATUSES.map((s, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={s.key} className="flex flex-col items-start" data-testid={`timeline-step-${s.key}`}>
              <div
                className={[
                  "relative w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 z-[1]",
                  isActive
                    ? "bg-[color:var(--atech-copper)] shadow-[0_0_0_6px_color-mix(in_srgb,var(--atech-copper)_15%,transparent)]"
                    : isPast
                      ? "bg-[color:var(--atech-text)] text-[color:var(--atech-bg)]"
                      : "bg-[color:var(--atech-surface)] border border-[color:var(--atech-border)]",
                ].join(" ")}
              >
                {isPast ? (
                  <Check size={14} className="text-[color:var(--atech-bg)]" strokeWidth={3} />
                ) : isActive ? (
                  <span className="w-2 h-2 rounded-full bg-white keep-white" />
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--atech-text-muted)]" />
                )}
              </div>
              <div className="mt-3 space-y-0.5">
                <div
                  className={`text-[11px] font-medium tracking-wide ${
                    isActive ? "text-[color:var(--atech-text)]" : isPast ? "text-[color:var(--atech-text)]" : "text-[color:var(--atech-text-muted)]"
                  }`}
                >
                  {s.label}
                </div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)]">
                  Stage {i + 1}
                </div>
                {isActive && captionFor(s.key) && (
                  <p
                    className="text-[11px] text-[color:var(--atech-text-dim)] leading-relaxed mt-1.5 max-w-[160px]"
                    data-testid={`timeline-caption-${s.key}`}
                  >
                    {captionFor(s.key)}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default StatusTimeline;
